import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { LocalizationProvider } from 'nucleus-text';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import CookieBanner from './CookieBanner';
import CookieModal from './CookieModal';
import { getConsentData, setConsentData } from './localStorageUtils';
import { defaultLocale } from './locales';
import { locales, resolveLocales } from './locales/localeUtils';
export const CookieConsentContext = createContext(undefined);
/**
 * If your app already has a LocalizationProvider: use this wrapper, import the locales from
 * this package and pass them into your app's localization provider along with your existing app locales.
 * This provider includes only what is necessary for cookie consent EXCEPT the localization provider
 * Localization must be provided by the consumer to use this wrapper
 * This is to provide flexibility to the provider structure of consumers
 */
export const CookieConsentProviderWrapper = ({ children, migrate, siteIdentifier, baseContext }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const consentString = getConsentData(siteIdentifier);
    let initialConsentState = consentString
        ? JSON.parse(consentString)
        : { Performance: false, Functionality: false, Marketing: false };
    if (!consentString && typeof migrate === 'function') {
        const migratedState = migrate();
        if (migratedState) {
            initialConsentState = migratedState;
            setConsentData(migratedState, siteIdentifier);
        }
    }
    const [cookieConsent, setCookieConsent] = useState(initialConsentState);
    const values = useMemo(() => (Object.assign(Object.assign({ isModalOpen,
        setIsModalOpen,
        setCookieConsent,
        siteIdentifier }, cookieConsent), baseContext)), [baseContext, cookieConsent, setCookieConsent, siteIdentifier, isModalOpen, setIsModalOpen]);
    return _jsx(CookieConsentContext.Provider, Object.assign({ value: values }, { children: children }));
};
/**
 * This provider includes only what is absolutely necessary for cookie consent to work.
 * This is useful for storybooks or tests where you only want one component (CookieBanner or CookieModal) to show up.
 */
export const BaseCookieConsentProvider = ({ children, migrate, siteIdentifier, baseContext, locale = defaultLocale, textOverrides }) => {
    // if user selected locale does not exist, fall back to defaultLocale.
    const selectedLocale = locales[locale] ? locale : defaultLocale;
    const localesWithOverrides = Object.entries(locales)
        .filter(localeEntry => localeEntry[0] === selectedLocale)
        .reduce((acc, [localeKey, localeTranslations]) => {
        const localeOverrides = (textOverrides === null || textOverrides === void 0 ? void 0 : textOverrides[localeKey]) || {};
        return Object.assign(Object.assign({}, acc), { [localeKey]: resolveLocales(localeTranslations(), Object.assign(Object.assign({}, localeOverrides), { default: localeOverrides })) });
    }, {});
    return (_jsx(CookieConsentProviderWrapper, Object.assign({ migrate: migrate, siteIdentifier: siteIdentifier, baseContext: baseContext }, { children: _jsx(LocalizationProvider, Object.assign({ locale: selectedLocale, locales: localesWithOverrides }, { children: children })) })));
};
/**
 * Provider most consumers will use.
 * Do not use this provider if your application has a LocalizationProvider. Use CookieContextProviderWrapper instead.
 * Everything needed for cookie consent is included,
 * including the modal and the banner.
 * @param children - the children components to render
 * @param migrate - a function for migrating existing user's preferences if necessary
 * @param siteIdentifier - a string you can pass in if you want users to be able to set different preferences on different parts of your site.
 * @param hideBanner - if true, hides the banner. This is useful when gating the cookie banner behind an experiment.
 * @param bannerType - Cookie banner two options: "accept" - Only has an Accept button. "essential" - Has accept all and accept essential buttons. "customize" - Has accept all, accept essential, and customize preferences buttons.
 * @param hidePrivacyPolicyLink - If true, hides the privacy policy link.
 * @param locale - current language of cookie banner
 * For example, if you want preferences to be stored per-event, you can pass in the event id.
 */
const CookieConsentProvider = ({ children, migrate, siteIdentifier, hideBanner = false, bannerType = 'customize', hidePrivacyPolicyLink = false, locale, textOverrides, focusTrapOptions }) => {
    return (_jsxs(BaseCookieConsentProvider, Object.assign({ migrate: migrate, siteIdentifier: siteIdentifier, locale: locale, textOverrides: textOverrides }, { children: [children, _jsx(CookieModal, { focusTrapOptions: focusTrapOptions, hidePrivacyPolicyLink: hidePrivacyPolicyLink }), !hideBanner && (_jsx(CookieBanner, { bannerType: bannerType, hidePrivacyPolicyLink: hidePrivacyPolicyLink }))] })));
};
export const useCookieConsent = () => {
    const { Performance: canSetPerformanceCookies, Functionality: canSetFunctionalityCookies, Marketing: canSetMarketingCookies } = useContext(CookieConsentContext);
    return { canSetPerformanceCookies, canSetFunctionalityCookies, canSetMarketingCookies };
};
export const useSetCookieConsent = () => {
    const { setCookieConsent, siteIdentifier } = useContext(CookieConsentContext);
    const setConsentState = useCallback((data) => {
        setCookieConsent(data);
        setConsentData(data, siteIdentifier);
    }, [setCookieConsent, siteIdentifier]);
    return setConsentState;
};
export const useSiteIdentifier = () => {
    const { siteIdentifier } = useContext(CookieConsentContext);
    return siteIdentifier;
};
export const useOpenCookieModal = () => {
    const { setIsModalOpen } = useContext(CookieConsentContext);
    const openModal = useCallback(() => {
        setIsModalOpen(true);
    }, [setIsModalOpen]);
    return openModal;
};
export const useModalStatus = () => {
    const { isModalOpen, setIsModalOpen } = useContext(CookieConsentContext);
    return { isModalOpen, setIsModalOpen };
};
export default CookieConsentProvider;
//# sourceMappingURL=CookieConsentContext.js.map